import { create } from 'zustand';
import { buildWebAppRecord } from '@/webapp/webAppRecord';
import { chatStore, projectStore } from '@/storage/projectStorePort';
import { ApiError } from '../api/client';
import { buildApp } from '../api/build';
import { hostApp } from '../api/hosting';
import { useDraft } from './draft';

/**
 * A build, owned by the browser.
 *
 * The shared buildRunStore polls a queued job, keeps the screen awake, and branches across
 * three runtimes — and it reaches the React Native engine to do it, which is the only
 * reason this client ever needed a `registerAll` stub. The web needs none of that: the
 * server's /build is synchronous and returns the finished document, and the browser only
 * ever builds web apps.
 *
 * It lives outside the screen on purpose, so navigating away does not cancel a build.
 */
export type BuildStatus = 'idle' | 'running' | 'error';

export interface BuildRun {
  status: BuildStatus;
  /** What is happening, for the overlay. */
  phase: string;
  error: string | null;
  /** True when the failure is one retrying cannot fix. */
  terminal: boolean;
  start: (ownerId: string) => Promise<void>;
  retry: () => void;
  dismiss: () => void;
}

/** Where to go when a build finishes. Injected so the store owns no routing. */
let navigate: ((path: string) => void) | null = null;
export function setBuildNavigate(fn: ((path: string) => void) | null): void {
  navigate = fn;
}

let lastOwnerId: string | null = null;

export const useBuildRun = create<BuildRun>((set, get) => ({
  status: 'idle',
  phase: 'queued',
  error: null,
  terminal: false,

  start: async (ownerId: string) => {
    if (get().status === 'running') return;
    lastOwnerId = ownerId;

    const draft = useDraft.getState();
    const goal = draft.description.trim();
    if (!goal) {
      set({ status: 'error', error: 'Describe the app you want first.', terminal: true });
      return;
    }
    const appName = draft.appName || draft.plan?.appName || 'My app';

    set({ status: 'running', phase: 'generating', error: null, terminal: false });

    try {
      const built = await buildApp({
        goal,
        appName,
        vibe: draft.vibe ?? null,
        plan: draft.plan ?? null,
      });

      /*
       * Publishing is a SEPARATE step and its failure must not cost the build. The
       * generated source is the expensive part and it is already in hand; throwing it away
       * over a transient network error would lose real work. The project is created either
       * way and the summary says plainly whether there is a link yet.
       */
      set({ phase: 'publishing' });
      let siteUrl: string | undefined;
      let publishError: string | null = null;
      try {
        const host = await hostApp({ appId: built.appId, html: built.html });
        siteUrl = host.siteUrl;
      } catch (e) {
        publishError = e instanceof Error ? e.message : 'Publishing failed.';
      }

      const definition = buildWebAppRecord({
        // The SAME id the document was assembled under: it is the storage namespace, so a
        // different one would strand the data of anyone already using the app.
        id: built.appId,
        appName: built.appName || appName,
        goal,
        source: built.appCode,
        webUrl: siteUrl,
        vibe: draft.vibe,
        plan: draft.plan ?? null,
      });

      set({ phase: 'saving' });
      const project = await projectStore().create({ name: definition.name, description: goal, definition }, ownerId);

      const now = new Date().toISOString();
      await chatStore().addMessage(project.id, { role: 'user', content: goal, timestamp: now });
      await chatStore().addMessage(project.id, {
        role: 'assistant',
        content: siteUrl
          ? `I built **${definition.name}** as a web app you can open in any browser. Share the link with anyone.`
          : `I built **${definition.name}**, but couldn't publish it just yet — ${publishError} Your app is saved; try publishing again from the project.`,
        timestamp: new Date().toISOString(),
        appDefinitionSnapshot: JSON.stringify(definition),
      });

      set({ status: 'idle', phase: 'queued', error: null });
      navigate?.(`/project/${project.id}?mode=preview`);
    } catch (e) {
      // Out of credits and a refused prompt are not things retrying will fix, so the
      // overlay must not offer a button that cannot work.
      const terminal =
        e instanceof ApiError && (e.kind === 'out_of_credits' || e.kind === 'refused' || e.kind === 'unauthorized');
      set({
        status: 'error',
        error: e instanceof Error ? e.message : 'That build did not finish.',
        terminal,
      });
    }
  },

  retry: () => {
    if (!lastOwnerId) return;
    void get().start(lastOwnerId);
  },

  dismiss: () => set({ status: 'idle', phase: 'queued', error: null, terminal: false }),
}));
